import {
  type Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { TreeApi as ArboristTreeApi } from 'react-arborist';
import {
  type Actions,
  rootReducer,
} from 'react-arborist/dist/module/state/root-reducer';

import type { NodeData, TNode } from './Node';
import { getNode, getPathToNode, updateDescendantSelections } from './utils';

export type SelectionDescendantMap = Map<string, SelectionDescendantMap>;

type ExtendedActions = Actions | { type: 'OPEN_ALL' } | { type: 'CLOSE_ALL' };

const getExtendedActionType = (action: {
  type: string;
}): 'OPEN_ALL' | 'CLOSE_ALL' | null => {
  if (action.type === 'OPEN_ALL') return 'OPEN_ALL';
  if (action.type === 'CLOSE_ALL') return 'CLOSE_ALL';
  return null;
};

export type TreeData<T extends object = object> = NodeData &
  T & {
    children?: TreeData<T>[];
    disabled?: boolean;
    depth?: number;
  };

export type TreeApiBaseProps = {
  data: TreeData[];
  openByDefault?: boolean;
  initialSelections?: string[];
  singleSelect?: boolean;
};
type AllOrNone<T> = Required<T> | Partial<Record<keyof T, undefined>>;
export type TreeApiControlProps = AllOrNone<{
  value: string[];
  onChange: (value: string[], node?: TNode[]) => void;
}>;
export type TreeApiProps = TreeApiBaseProps & TreeApiControlProps;

export const useTreeApi = ({
  data,
  openByDefault = false,
  initialSelections,
  singleSelect = false,
  value,
  onChange,
}: TreeApiProps) => {
  const treeRef = useRef<ArboristTreeApi<TreeData>>();
  /** ================================
   * Selection
   ================================ */
  const [internalSelectedIds, setInternalSelectedIds] = useState<string[]>(
    value || initialSelections || []
  );

  const selectedIds = value || internalSelectedIds;

  const [indeterminateSelectedIds, setIndeterminateSelectedIds] = useState<
    string[]
  >([]);

  const selectionDescendantMap = useRef<SelectionDescendantMap>(new Map());

  const selectNodes = (nodes: TNode[]) => {
    const ids = nodes.map((node) => node.id);
    if (value && onChange) {
      if (singleSelect) onChange(ids, nodes);
      else onChange([...selectedIds, ...ids], nodes);
    } else {
      if (singleSelect) setInternalSelectedIds(ids);
      else {
        setInternalSelectedIds((prevSelectedIds) => [
          ...prevSelectedIds,
          ...ids,
        ]);
      }
    }
  };

  const deselectNodes = (nodes: TNode[]) => {
    const ids = nodes.map((node) => node.id);
    if (value && onChange) {
      onChange(
        selectedIds.filter((selectedId) => !ids.includes(selectedId)),
        nodes
      );
    } else {
      setInternalSelectedIds((prevSelectedIds) =>
        prevSelectedIds.filter((selectedId) => !ids.includes(selectedId))
      );
    }
  };

  const prevSelectionIds = useRef<string[]>([]);

  const runSelectionSync = useCallback(() => {
    const rootNode = treeRef.current?.root;
    if (!rootNode) return;

    const addedIds = selectedIds.filter(
      (id) => !prevSelectionIds.current.includes(id)
    );
    const removedIds = prevSelectionIds.current.filter(
      (id) => !selectedIds.includes(id)
    );

    prevSelectionIds.current = selectedIds;

    addedIds.forEach((id) => {
      const pathToNode = getPathToNode(id);
      const node = getNode({ nodes: rootNode.children, pathToNode });
      if (!node) return;
      setIndeterminateSelectedIds(() =>
        updateDescendantSelections({
          operation: 'INSERT',
          node,
          selectionDescendantMap: selectionDescendantMap.current,
        })
      );
    });

    removedIds.forEach((id) => {
      const pathToNode = getPathToNode(id);

      const node = getNode({ nodes: rootNode.children, pathToNode });

      if (!node) {
        setIndeterminateSelectedIds(() =>
          updateDescendantSelections({
            operation: 'REMOVE',
            ancestors: pathToNode,
            selectionDescendantMap: selectionDescendantMap.current,
            selectedIds,
          })
        );

        return;
      }

      setIndeterminateSelectedIds(() =>
        updateDescendantSelections({
          operation: 'REMOVE',
          node,
          selectionDescendantMap: selectionDescendantMap.current,
          selectedIds,
        })
      );
    });
  }, [selectedIds]);

  useEffect(() => {
    const rootNode = treeRef.current?.root;
    let rafId = 0;
    if (rootNode) {
      runSelectionSync();
    } else if (selectedIds.length > 0) {
      rafId = requestAnimationFrame(() => {
        runSelectionSync();
      });
    }
    return () => {
      if (rafId !== 0) cancelAnimationFrame(rafId);
    };
  }, [selectedIds, runSelectionSync]);

  const clearAll = () => {
    if (value && onChange) {
      onChange([]);
    } else {
      setInternalSelectedIds([]);
    }
  };

  // When the underlying tree data changes (e.g., taxonomy switch),
  // reset the descendant map and recompute indeterminate parent state
  // from the current selected ids against the new tree.
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const rootNode = treeRef.current?.root;
    if (!rootNode) return;

    selectionDescendantMap.current = new Map();

    let nextIndeterminate: string[] = [];
    selectedIds.forEach((id) => {
      const pathToNode = getPathToNode(id);
      const node = getNode({ nodes: rootNode.children, pathToNode });
      if (!node) return;
      nextIndeterminate = updateDescendantSelections({
        operation: 'INSERT',
        node,
        selectionDescendantMap: selectionDescendantMap.current,
      });
    });

    setIndeterminateSelectedIds(nextIndeterminate);
    prevSelectionIds.current = selectedIds;
    // DO NOT REMOVE data, even thought it's not used in the hook, we want taxonomy changes to trigger this effect
  }, [data, selectedIds]);

  const toggleNodeSelection = (id: string) => {
    const pathToNode = getPathToNode(id);
    const rootNode = treeRef.current?.root;
    const node = getNode({ nodes: rootNode?.children || null, pathToNode });

    if (!node) return;

    const { data } = node;

    const isSelected = selectedIds.includes(id);

    /** Should send selections in a single request here */
    const linkedNodes: TNode[] =
      data.linkedIds
        ?.map((linkedId): TNode | null => {
          const rootNode = treeRef.current?.root;
          if (!rootNode) return null;

          const pathToNode = getPathToNode(linkedId);
          return getNode({ nodes: rootNode?.children, pathToNode });
        })
        .filter((node) => node !== null) || [];

    if (!isSelected) selectNodes([node, ...linkedNodes]);
    else deselectNodes([node, ...linkedNodes]);
  };

  /** ================================
   * Visibility
   ================================ */
  const allNonLeafNodeIds = useMemo(() => {
    const getChildIds = (nodes: TreeData[]): string[] => {
      return nodes.flatMap((node) => {
        if (node.children) return [node.id, ...getChildIds(node.children)];
        return [];
      });
    };
    return getChildIds(data);
  }, [data]);

  /** ======== Extend Reducer ======== */
  // Installs a custom reducer that handles OPEN_ALL/CLOSE_ALL. Called inline
  // from openAll/closeAll because react-arborist mounts its Tree on a second
  // render (via a ref-callback in TreeSelection), so treeRef.current isn't
  // available on the initial commit. By installing right before dispatching,
  // we guarantee the reducer is in place when we actually need it.
  // The ref tracks the deps the currently-installed reducer was built from
  // so we skip the work (and avoid the internal @@redux/REPLACE dispatch)
  // when the data hasn't changed since the last install.
  const installedForRef = useRef<string[] | null>(null);
  const installCustomReducer = useCallback(() => {
    const treeApi = treeRef.current;
    if (!treeApi) return;
    if (installedForRef.current === allNonLeafNodeIds) return;
    installedForRef.current = allNonLeafNodeIds;
    treeApi.store.replaceReducer((state, action) => {
      if (!state) return rootReducer(state, action);

      const extendedType = getExtendedActionType(action);
      if (extendedType !== null) {
        const isFiltered = treeApi.isFiltered;
        const openByDefault = treeApi.props.openByDefault;

        const isOpen = extendedType === 'OPEN_ALL';

        const clearFilterState =
          (isFiltered && isOpen) ||
          (!isFiltered && openByDefault && isOpen) ||
          (!isFiltered && !openByDefault && !isOpen);

        const filteredState = clearFilterState
          ? {}
          : allNonLeafNodeIds.reduce<{
              [key: string]: boolean;
            }>((acc, id) => {
              acc[id] = isOpen;
              return acc;
            }, {});

        const newState = {
          ...state,
          nodes: {
            ...state.nodes,
            open: {
              ...state.nodes.open,
              ...(isFiltered
                ? { filtered: filteredState }
                : { unfiltered: filteredState }),
            },
          },
        };
        return newState;
      } else {
        return rootReducer(state, action);
      }
    });
  }, [allNonLeafNodeIds]);

  /** ======== Expand/Collapse ======== */
  const [isExpanded, setIsExpanded] = useState<boolean>(openByDefault);
  const [userIntent, setUserIntent] = useState<
    'auto' | 'collapsed' | 'expanded'
  >(openByDefault ? 'expanded' : 'auto');

  const openAll = () => {
    installCustomReducer();
    (treeRef.current?.store.dispatch as Dispatch<ExtendedActions>)({
      type: 'OPEN_ALL',
    });
  };

  const closeAll = () => {
    installCustomReducer();
    (treeRef.current?.store.dispatch as Dispatch<ExtendedActions>)({
      type: 'CLOSE_ALL',
    });
  };
  const expand = () => {
    setUserIntent('expanded');
    setIsExpanded(true);
    openAll();
  };
  const collapse = () => {
    setUserIntent('collapsed');
    setIsExpanded(false);
    closeAll();
  };

  /** ================================
   * Search
   ================================ */
  const [search, setSearch] = useState<string>('');

  const onSearch = (search: string) => {
    const newSearchLength = search.trim().length;
    setSearch(search);

    if (newSearchLength === 0) {
      // Search cleared
      setIsExpanded(false);
      setUserIntent('auto'); // Reset intent
    } else {
      // Search is active
      // Only expand if the user hasn't explicitly collapsed it.
      if (userIntent !== 'collapsed') {
        setIsExpanded(true);
      }
    }
  };

  return {
    treeRef,
    selectedIds,
    indeterminateSelectedIds,
    toggleNodeSelection,
    isExpanded,
    expand,
    collapse,
    search,
    onSearch,
    clearAll,
  };
};

export type TreeApi = ReturnType<typeof useTreeApi>;
